
import React, { useState } from 'react';
import type { ChatMessage, AIQuestion } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { validateQuestionResponse } from '../services/inputValidationService';

interface ChatDisplayProps {
    history: ChatMessage[];
    currentQuestion: AIQuestion | null;
    onAnswer: (answer: string) => void;
    isLoading: boolean;
}

const ChatBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
    const isUser = message.role === 'user';
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-md px-4 py-2 rounded-lg ${isUser ? 'bg-[#002A5C] text-white' : 'bg-gray-200 text-gray-800'}`}>
                <p className="text-sm">{message.text}</p>
            </div>
        </div>
    );
};

const ChatDisplay: React.FC<ChatDisplayProps> = ({ history, currentQuestion, onAnswer, isLoading }) => {
    const [customInput, setCustomInput] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);
    
    const handleCustomSubmit = () => {
        const trimmedInput = customInput.trim();
        if (!trimmedInput) return;
        
        // Validate the input
        if (currentQuestion) {
            const validation = validateQuestionResponse(trimmedInput, currentQuestion.questionText);
            if (!validation.isValid) {
                setValidationError(validation.errorMessage || 'Please provide a valid response.');
                return;
            }
        }
        
        // Clear any previous validation errors
        setValidationError(null);
        
        // Submit the answer
        onAnswer(trimmedInput);
        setCustomInput('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleCustomSubmit();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomInput(e.target.value);
        // Clear validation error when user starts typing
        if (validationError) {
            setValidationError(null);
        }
    };
    
    return (
        <div className="p-4 sm:p-6">
            <div className="space-y-4 mb-6 h-64 overflow-y-auto pr-2">
                {history.map((msg, index) => <ChatBubble key={index} message={msg} />)}
            </div>
            
            <div className="border-t border-gray-200 pt-4">
                {isLoading && (
                    <div className="flex justify-center items-center" style={{ minHeight: '8rem' }}>
                        <LoadingSpinner />
                    </div>
                )}

                {!isLoading && currentQuestion && (
                    <div>
                        <h3 className="text-md font-semibold text-gray-700 mb-4 text-center">{currentQuestion.questionText}</h3>
                        
                        <div className="space-y-3 mt-4">
                            {currentQuestion.options.map((option) => (
                                <button
                                    key={option}
                                    onClick={() => onAnswer(option)}
                                    disabled={isLoading}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#002A5C] hover:bg-[#001F44] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                    aria-label={`Answer: ${option}`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                        
                        {/* Custom Input Option */}
                        <div className="mt-6 pt-4 border-t border-gray-200">
                            <p className="text-sm text-gray-600 mb-3 text-center">Or type your own response:</p>
                            
                            {/* Validation Error Display */}
                            {validationError && (
                                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                                    <p className="text-sm text-red-600">{validationError}</p>
                                </div>
                            )}
                            
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={customInput}
                                    onChange={handleInputChange}
                                    onKeyPress={handleKeyPress}
                                    disabled={isLoading}
                                    placeholder="Enter your response..."
                                    className={`flex-1 px-3 py-2 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                        validationError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                    }`}
                                />
                                <button
                                    onClick={handleCustomSubmit}
                                    disabled={isLoading || !customInput.trim()}
                                    className="px-4 py-2 bg-[#002A5C] text-white text-sm font-medium rounded-md shadow-sm hover:bg-[#001F44] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                >
                                    Send
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatDisplay;
